import React, { useState, useEffect } from 'react';
import { getUserById, updateUser } from '../api/userService';
import './EditUserModal.css';

const EditUserModal = ({ isOpen, onClose, userId, onUserUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    website: '',
    role: 'user',
  });

  useEffect(() => {
    if (isOpen && userId) {
      loadUser();
    }
  }, [isOpen, userId]);

  const loadUser = async () => {
    try {
      setLoadingUser(true);
      setError('');
      const response = await getUserById(userId);
      const user = response.user || response;
      
      setFormData({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        website: user.website || '',
        role: user.role || 'user',
      });
    } catch (err) {
      console.error('Failed to load user:', err);
      setError(err.response?.data?.message || 'Failed to load user details.');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validation
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required.');
      setLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    try {
      await updateUser(userId, formData);
      setMessage('User updated successfully!');
      
      // Notify parent to refresh user list
      if (onUserUpdated) {
        setTimeout(() => {
          onUserUpdated();
          onClose();
        }, 1000);
      } else {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.response?.data?.message || 'Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'user', label: 'Viewer' },
    { value: 'technician', label: 'Developer' },
    { value: 'manager', label: 'Manager' },
    { value: 'admin', label: 'Administrator' },
  ];

  if (!isOpen) return null;

  return (
    <div className="edit-user-modal-overlay" onClick={onClose}>
      <div className="edit-user-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="edit-user-modal-header">
          <h2>Edit User</h2>
          <button type="button" className="edit-user-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {error && (
          <div className="wp-notice wp-notice-error">
            <p>{error}</p>
          </div>
        )}

        {message && (
          <div className="wp-notice wp-notice-success">
            <p>{message}</p>
          </div>
        )}

        {loadingUser ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            Loading user details...
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <table className="wp-form-table">
              <tbody>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-username">Username</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="edit-username"
                      className="wp-form-input"
                      value={formData.username}
                      disabled
                    />
                    <p className="wp-info-text">Usernames cannot be changed.</p>
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-email">
                      Email <span className="wp-required">(required)</span>
                    </label>
                  </th>
                  <td>
                    <input
                      type="email"
                      id="edit-email"
                      className="wp-form-input"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-firstName">First Name</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="edit-firstName"
                      className="wp-form-input"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      autoComplete="off"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-lastName">Last Name</label>
                  </th>
                  <td>
                    <input
                      type="text"
                      id="edit-lastName"
                      className="wp-form-input"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      autoComplete="off"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-website">Website</label>
                  </th>
                  <td>
                    <input
                      type="url"
                      id="edit-website"
                      className="wp-form-input"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="http://"
                      autoComplete="off"
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row">
                    <label htmlFor="edit-role">Role</label>
                  </th>
                  <td>
                    <select
                      id="edit-role"
                      className="wp-form-select"
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                    >
                      {roleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="edit-user-modal-footer">
              <button type="button" className="wp-button-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="wp-button-primary" disabled={loading}>
                {loading ? 'Updating User...' : 'Update User'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditUserModal;

